/**
 * ADM-15 收入统计页：按原型 RevenuePage 逐区块转译，筛选项/列/按钮/文案不得增删。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Segmented, Select, Space, Table, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import { orgCampusApi, type CampusItem } from '../../api/campuses.api';
import { useListExport } from '../../hooks/useListExport';
import { useHasPermission } from '../../auth/HasPermission';
import {
  billingApi,
  type OrderListItem,
  type RevenueSummary,
  type SkuRevenueBreakdownItem,
} from '../../api/billing.api';
import { ApiError } from '../../api/transport';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { OrderDetailDrawer } from './OrderDetailDrawer';
import { OrderStatusTag } from './OrderStatusTag';

const { Title } = Typography;

const EMPTY_SUMMARY: RevenueSummary = {
  campusId: null,
  dateFrom: null,
  dateTo: null,
  receivableAmountMinor: 0,
  paidAmountMinor: 0,
  revenueAmountMinor: 0,
  unpaidAmountMinor: 0,
  reversedAmountMinor: 0,
  discountAmountMinor: 0,
};

/** 对齐原型 formatAmount：¥ 后无空格、两位小数。 */
function formatAmount(fen: number): string {
  return `¥${(fen / 100).toFixed(2)}`;
}

/** ADM-15 收入统计。 */
export function RevenuePage() {
  const [campusId, setCampusId] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [view, setView] = useState<'summary' | 'detail'>('summary');
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);
  const [campuses, setCampuses] = useState<CampusItem[]>([]);
  const [summary, setSummary] = useState<RevenueSummary>(EMPTY_SUMMARY);
  const [skuDetail, setSkuDetail] = useState<SkuRevenueBreakdownItem[]>([]);
  const [recentOrders, setRecentOrders] = useState<OrderListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const canExport = useHasPermission('ADM-15', 'EXPORT');
  const { exporting: exportingSku, runExport } = useListExport('revenue-sku');

  useEffect(() => {
    orgCampusApi
      .listCampuses({ page: 1, pageSize: 100 })
      .then((result) => setCampuses(result.items))
      .catch(() => undefined);
  }, []);

  const query = useMemo(
    () => ({
      campusId,
      dateFrom: dateRange?.[0]?.format('YYYY-MM-DD'),
      dateTo: dateRange?.[1]?.format('YYYY-MM-DD'),
    }),
    [campusId, dateRange],
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      const [nextSummary, breakdown, orders] = await Promise.all([
        billingApi.getRevenueSummary(query),
        billingApi.getSkuRevenueBreakdown(query),
        billingApi.listOrders({
          campusId: query.campusId,
          createdFrom: query.dateFrom,
          createdTo: query.dateTo,
          excludeStatuses: ['CANCELLED', 'EXPIRED'],
          page: 1,
          pageSize: 20,
        }),
      ]);
      setSummary(nextSummary);
      setSkuDetail(breakdown.items);
      setRecentOrders(orders.items.slice(0, 10));
    } catch (err) {
      setSummary(EMPTY_SUMMARY);
      setSkuDetail([]);
      setRecentOrders([]);
      setError(err instanceof ApiError ? err.message : '收入统计加载失败');
    }
  }, [query]);

  useEffect(() => {
    void load();
  }, [load]);

  const detailColumns = useResizableColumns<SkuRevenueBreakdownItem>([
    { title: 'SKU编码', dataIndex: 'skuCode' },
    { title: 'SKU名称', dataIndex: 'skuName' },
    { title: '订单数', dataIndex: 'orderCount' },
    { title: '学生数', dataIndex: 'studentCount' },
    { title: '应收', dataIndex: 'receivableAmountMinor', render: (v: number) => formatAmount(v) },
    { title: '实收', dataIndex: 'paidAmountMinor', render: (v: number) => formatAmount(v) },
    { title: '收入', dataIndex: 'revenueAmountMinor', render: (v: number) => formatAmount(v) },
    { title: '未收', dataIndex: 'unpaidAmountMinor', render: (v: number) => formatAmount(v) },
    { title: '冲正', dataIndex: 'reversedAmountMinor', render: (v: number) => formatAmount(v) },
  ]);

  const summaryColumns = useResizableColumns<OrderListItem>([
    {
      title: '订单号',
      dataIndex: 'orderNo',
      render: (v: string, r) => <a onClick={() => setActiveOrderId(r.orderId)}>{v}</a>,
    },
    { title: '商品', dataIndex: 'productSnapshot', render: (v: OrderListItem['productSnapshot']) => v.name },
    { title: '应收', dataIndex: 'receivableAmount', render: (v: number) => formatAmount(v) },
    { title: '状态', dataIndex: 'status', render: (v: OrderListItem['status']) => <OrderStatusTag status={v} /> },
  ]);

  return (
    <div>
      <div className="page-header-row">
        <div>
          <Title level={3}>收入统计</Title>
          <p className="page-header-desc">按校区与课程SKU查看应收、实收、收入与冲正口径统计。</p>
        </div>
      </div>
      <div className="page-filter-card">
        <Space wrap size={12}>
          <Select
            placeholder="校区"
            allowClear
            style={{ width: 160 }}
            value={campusId}
            onChange={setCampusId}
            options={campuses.map((c) => ({ label: c.name, value: c.id }))}
          />
          <DatePicker.RangePicker
            placeholder={['订单创建开始', '截止']}
            value={dateRange}
            onChange={(v) => setDateRange(v as [Dayjs, Dayjs] | null)}
          />
          <Segmented
            value={view}
            onChange={(v) => setView(v as 'summary' | 'detail')}
            options={[
              { label: '趋势/汇总', value: 'summary' },
              { label: 'SKU明细', value: 'detail' },
            ]}
          />
          <Button
            onClick={() => {
              setCampusId(undefined);
              setDateRange(null);
            }}
          >
            重置
          </Button>
          {canExport && (
            <Button
              loading={exportingSku}
              disabled={view !== 'detail'}
              title={view !== 'detail' ? '导出为当前筛选的 SKU 明细，请切换到「SKU明细」视图' : undefined}
              onClick={() =>
                void runExport({
                  campusId: campusId ?? undefined,
                  dateFrom: dateRange?.[0]?.format('YYYY-MM-DD'),
                  dateTo: dateRange?.[1]?.format('YYYY-MM-DD'),
                })
              }
            >
              导出
            </Button>
          )}
        </Space>
      </div>

      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}

      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-label">应收款</div>
          <div className="stat-value">{formatAmount(summary.receivableAmountMinor)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">实收款</div>
          <div className="stat-value">{formatAmount(summary.paidAmountMinor)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">收入</div>
          <div className="stat-value">{formatAmount(summary.revenueAmountMinor)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">未收金额</div>
          <div className="stat-value">{formatAmount(summary.unpaidAmountMinor)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">冲正金额</div>
          <div className="stat-value">{formatAmount(summary.reversedAmountMinor)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">减免</div>
          <div className="stat-value">{formatAmount(summary.discountAmountMinor)}</div>
        </div>
      </div>

      {view === 'detail' && (
        <div className="page-content-card">
          <Table
            rowKey="skuId"
            dataSource={skuDetail}
            pagination={false}
            columns={detailColumns.columns}
            components={detailColumns.components}
          />
        </div>
      )}

      {view === 'summary' && (
        <div className="page-content-card">
          <Table
            rowKey="orderId"
            dataSource={recentOrders}
            pagination={false}
            title={() => '近期订单下钻'}
            columns={summaryColumns.columns}
            components={summaryColumns.components}
          />
        </div>
      )}
      {activeOrderId && <OrderDetailDrawer orderId={activeOrderId} onClose={() => setActiveOrderId(null)} />}
    </div>
  );
}
